import {NavigationBarItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/navigation_bar.types';
import {useIsRTL} from '@aboutyou/i18n';
import {
  UnstyledButton,
  COLORS,
  GenericIconName,
  Icon,
  useSlider,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import React, {forwardRef, useRef} from 'react';
import {useRect} from 'react-viewport-utils';
import {styled} from '@linaria/react';
import {NAVIGATION_HEIGHT, SEARCH_BAR_TABLET_WIDTH} from '../../constants';
import {useShouldDisplaySlider} from '../hooks/useShouldDisplaySlider';
import {CategoryNavigationItem} from './CategoryNavigationItem';
import {useIsFeatureActive} from '@aboutyou/feature-flags';

interface CategoryNavigationItemsProps {
  activeNavItemIndex: number | undefined;
  items: NavigationBarItem[];
  onEnterNavigationItem: (index: number) => () => void;
}

export const CategoryNavigationItems: React.FC<
  CategoryNavigationItemsProps & {isSearchBarExpanded: boolean}
> = ({
  activeNavItemIndex,
  items,
  onEnterNavigationItem,
  isSearchBarExpanded,
}) => {
  const navItemsWrapperRef = useRef(null);
  const navItemsWrapperRect = useRect(navItemsWrapperRef, {
    disableScrollUpdates: true,
    priority: 'low',
  });
  const shouldDisplaySlider = useShouldDisplaySlider(
    navItemsWrapperRect,
    isSearchBarExpanded,
  );

  const scrollWrapperRef = React.useRef<HTMLDivElement>(null);
  const isRTL = useIsRTL();
  const {next, prev, nextIsActive, prevIsActive} = useSlider(scrollWrapperRef);
  const isNextActive = isRTL ? prevIsActive : nextIsActive;
  const isPrevActive = isRTL ? nextIsActive : prevIsActive;

  return (
    <Wrapper data-testid="topCategoryNavigationItems">
      <PreviousButton
        data-testid="topCategoryNavigationItems-prev-btn"
        hidden={!isPrevActive || !shouldDisplaySlider}
        onClick={() => isPrevActive && prev()}
      >
        <Icon size="S" asset={GenericIconName.ArrowLeft} />
      </PreviousButton>
      <ScrollableWrapper ref={scrollWrapperRef}>
        <NavItemsWithRef
          activeNavItemIndex={activeNavItemIndex}
          items={items}
          onEnterNavigationItem={onEnterNavigationItem}
          ref={navItemsWrapperRef}
        />
      </ScrollableWrapper>
      <NextButton
        data-testid="topCategoryNavigationItems-next-btn"
        hidden={!isNextActive || !shouldDisplaySlider}
        onClick={() => isNextActive && next()}
      >
        <Icon size="S" asset={GenericIconName.ArrowRight} />
      </NextButton>
    </Wrapper>
  );
};

const NavItemsWithRef = forwardRef<
  HTMLUListElement,
  CategoryNavigationItemsProps
>(function NavItemsWithRef(
  {activeNavItemIndex, items, onEnterNavigationItem},
  ref,
) {
  const {isActive: isSaleIndicationActive} = useIsFeatureActive(
    'desktop_nav_sale_indications',
  );
  return (
    <WrapperInner ref={ref} data-testid="topCategoriesContainer">
      {items.map(
        (
          {
            badges,
            label,
            url: linkTarget,
            textColor,
            gradientTextColors,
            saleIcon,
          },
          index,
        ) => (
          <CategoryNavigationItem
            badges={badges}
            isActive={index === activeNavItemIndex}
            itemHasContent={!!items[index]?.flyout}
            key={`${label}-${index}`}
            label={label}
            onEnterNavigationItem={onEnterNavigationItem(index)}
            textColor={textColor}
            url={linkTarget}
            gradientTextColors={gradientTextColors}
            saleIcon={isSaleIndicationActive ? saleIcon : undefined}
          />
        ),
      )}
    </WrapperInner>
  );
});

const Wrapper = styled.div`
  position: relative;
  left: 0;
  max-width: calc(100% - ${SEARCH_BAR_TABLET_WIDTH}px);
  height: 100%;
`;

const ButtonBase = styled(UnstyledButton)`
  position: absolute;
  top: 0;
  background: ${COLORS.White} !important;
  width: 32px;
  height: 100%;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.16);
  z-index: ${Z_INDEXES.TADARIDA_HEADER_NAVIGATION_BUTTONS};
  & > svg {
    position: absolute;
    top: ${NAVIGATION_HEIGHT / 2 - 13}px;
    left: 5px;
  }
`;

const PreviousButton = styled(ButtonBase)`
  left: -20px;
  [dir='rtl'] & {
    right: 0px;
  }
`;

const NextButton = styled(ButtonBase)`
  right: -1px;
  [dir='rtl'] {
    left: -20px;
  }
`;

const ScrollableWrapper = styled.div`
  overflow-x: scroll;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
  margin: auto;
`;

const WrapperInner = styled.ul`
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin: auto;
  height: 100%;
`;
